/**
 * useGrantGrid Hook
 * Manages state for the visual permission grid
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  loadGridData,
  saveGridChanges,
  toggleCellState,
  applyBulkAction,
  triggerPermissionRecalculation,
  CELL_STATES,
  ACTIONS
} from '../services/grantGridService';
import logger from 'utils/logger';

/**
 * Custom hook for managing grant grid state
 * @param {string} clientId - Client ID
 * @returns {Object} Grid state and operations
 */
export function useGrantGrid(clientId) {
  // Grid data state
  const [resources, setResources] = useState([]);
  const [grants, setGrants] = useState([]);
  const [gridState, setGridState] = useState(new Map());
  const [originalGridState, setOriginalGridState] = useState(new Map());

  // Subject criteria state (v5 schema)
  const [subjectCriteria, setSubjectCriteria] = useState({
    subjectType: 'ROLE',
    subjectValue: null
  });

  // Scope settings per resource (Map<resourceId, {includeChildren, includePeers, includeParent}>)
  const [scopeSettings, setScopeSettings] = useState(new Map());

  // Selection state for bulk operations
  const [selectedCells, setSelectedCells] = useState([]);

  // Loading and error state
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Track if grid has unsaved changes
  const hasChanges = useMemo(() => {
    if (gridState.size === 0 || originalGridState.size === 0) return false;

    for (const [resourceId, resourceRow] of gridState) {
      const originalRow = originalGridState.get(resourceId);
      if (!originalRow) return true;

      for (const [action, cell] of resourceRow) {
        const originalCell = originalRow.get(action);
        if (!originalCell || cell.state !== originalCell.state) {
          return true;
        }
      }
    }

    return false;
  }, [gridState, originalGridState]);

  /**
   * Load grid data based on current subject criteria
   */
  const loadGrid = useCallback(async () => {
    if (!clientId) return;

    try {
      setIsLoading(true);
      setError(null);

      const data = await loadGridData(clientId, subjectCriteria);

      setResources(data.resources);
      setGrants(data.grants);
      setGridState(new Map(data.gridState));
      setOriginalGridState(new Map(data.gridState));

      // Initialize scope settings for all resources
      const newScopeSettings = new Map();
      data.resources.forEach(resource => {
        newScopeSettings.set(resource.resourceId, {
          includeChildren: false,
          includePeers: false,
          includeParent: false
        });
      });
      setScopeSettings(newScopeSettings);

      logger.info('Grid loaded successfully', {
        resourceCount: data.resources.length,
        grantCount: data.grants.length
      });
    } catch (err) {
      logger.error('Failed to load grid', { error: err });
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, subjectCriteria]);

  /**
   * Reload grid when client or subject criteria changes
   */
  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  /**
   * Update subject criteria
   */
  const updateSubjectCriteria = useCallback((updates) => {
    setSubjectCriteria(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Load grid from an existing grant
   * Populates subject criteria from the grant and loads the grid
   */
  const loadFromGrant = useCallback((grant) => {
    if (!grant) return;

    logger.info('Loading grid from grant', { grantId: grant.id, grantName: grant.name });

    // Extract subject criteria from grant (v5 schema)
    const newSubjectCriteria = {
      subjectType: grant.subjectType || 'ROLE',
      subjectValue: grant.subjectValue || null
    };

    logger.info('Subject criteria extracted from grant (v5 schema)', newSubjectCriteria);

    // Update subject criteria (this will trigger grid reload via useEffect)
    setSubjectCriteria(newSubjectCriteria);
  }, []);

  /**
   * Toggle a specific cell's state
   */
  const toggleCell = useCallback((resourceId, action) => {
    setGridState(prev => {
      const newGridState = new Map(prev);
      const resourceRow = newGridState.get(resourceId);

      if (resourceRow) {
        const cell = resourceRow.get(action);
        if (cell) {
          const newState = toggleCellState(cell);
          cell.state = newState;
        }
      }

      return newGridState;
    });
  }, []);

  /**
   * Update scope settings for a resource
   */
  const updateScopeSettings = useCallback((resourceId, settings) => {
    setScopeSettings(prev => {
      const newSettings = new Map(prev);
      newSettings.set(resourceId, {
        ...prev.get(resourceId),
        ...settings
      });
      return newSettings;
    });
  }, []);

  /**
   * Toggle cell selection for bulk operations
   */
  const toggleCellSelection = useCallback((resourceId, action) => {
    setSelectedCells(prev => {
      const existing = prev.findIndex(
        cell => cell.resourceId === resourceId && cell.action === action
      );

      if (existing >= 0) {
        // Remove from selection
        return prev.filter((_, index) => index !== existing);
      } else {
        // Add to selection
        return [...prev, { resourceId, action }];
      }
    });
  }, []);

  /**
   * Select all cells
   */
  const selectAllCells = useCallback(() => {
    const allCells = [];
    for (const [resourceId] of gridState) {
      ACTIONS.forEach(action => {
        allCells.push({ resourceId, action });
      });
    }
    setSelectedCells(allCells);
  }, [gridState]);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedCells([]);
  }, []);

  /**
   * Apply bulk action to selected cells
   */
  const applyBulk = useCallback((bulkAction) => {
    if (selectedCells.length === 0) return;

    const newGridState = applyBulkAction(gridState, selectedCells, bulkAction);
    setGridState(newGridState);
    setSelectedCells([]);
  }, [gridState, selectedCells]);

  /**
   * Reset grid to original state (discard changes)
   */
  const resetGrid = useCallback(() => {
    setGridState(new Map(originalGridState));
    setSelectedCells([]);
  }, [originalGridState]);

  /**
   * Save grid changes
   */
  const saveGrid = useCallback(async () => {
    try {
      setIsSaving(true);
      setError(null);

      const results = await saveGridChanges(
        clientId,
        subjectCriteria,
        gridState,
        originalGridState,
        scopeSettings,
        resources
      );

      // Trigger permission recalculation
      await triggerPermissionRecalculation(clientId, subjectCriteria);

      // Reload grid to get fresh data
      await loadGrid();

      setSuccess(`Successfully saved changes: ${results.created} created, ${results.updated} updated, ${results.deleted} deleted`);

      if (results.errors.length > 0) {
        logger.warn('Some changes failed', { errors: results.errors });
        setError(`${results.errors.length} changes failed. Check console for details.`);
      }
    } catch (err) {
      logger.error('Failed to save grid', { error: err });
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [clientId, subjectCriteria, gridState, originalGridState, scopeSettings, resources, loadGrid]);

  /**
   * Get cell state for a specific resource and action
   */
  const getCellState = useCallback((resourceId, action) => {
    const resourceRow = gridState.get(resourceId);
    if (!resourceRow) return { state: CELL_STATES.NONE, grants: [] };

    const cell = resourceRow.get(action);
    if (!cell) return { state: CELL_STATES.NONE, grants: [] };

    return cell;
  }, [gridState]);

  /**
   * Check if a cell is selected
   */
  const isCellSelected = useCallback((resourceId, action) => {
    return selectedCells.some(
      cell => cell.resourceId === resourceId && cell.action === action
    );
  }, [selectedCells]);

  return {
    // Data
    resources,
    grants,
    gridState,
    subjectCriteria,
    scopeSettings,
    selectedCells,

    // State flags
    isLoading,
    isSaving,
    error,
    success,
    hasChanges,

    // Operations
    loadGrid,
    updateSubjectCriteria,
    loadFromGrant,
    toggleCell,
    updateScopeSettings,
    toggleCellSelection,
    selectAllCells,
    clearSelection,
    applyBulk,
    resetGrid,
    saveGrid,
    getCellState,
    isCellSelected,

    // Clear messages
    clearError: () => setError(null),
    clearSuccess: () => setSuccess(null)
  };
}
